import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateDailyTransferMutation } from '../../hooks/mutations/useDailyTransfersMutation';
import { useUpdateDailyTruckPlannedRankUpDownMutation } from '../../hooks/mutations/useDailyTrucksPlannedMutation';
import { useTheme } from '../../hooks/useTheme';
import { DataItem } from '../../services/apiSLTP/activities';
import { createDailyTransfer, DailyTransfer } from '../../services/apiSLTP/dailyReports/dailyTransfers';
import {
    createDailyTruckPlanned,
    DailyTruckPlanned,
    deleteDailyTruckPlanned,
    updateDailyTruckPlanned,
} from '../../services/apiSLTP/dailyReports/dailyTrucksPlanned';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { stepModes, truckMode } from '../../utils/enums';
import DroppableCell from '../DroppableCell';
import AssignTruckPlannedModal from '../modals/operations/planning/AssignTruckPlannedModal';
import ManageDailyTransferModal from '../modals/operations/planning/ManageDailyTransferModal';
import ManageDailyTruckPlannedModal from '../modals/operations/planning/ManageDailyTruckPlannedModal';
import IconButton from '../shared/IconButton';
import TruckPlannedItem from './TruckPlannedItem';

interface TrucksPlannedCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    refetch?: () => void;
    hiddenPlusIcon?: boolean;
    group?: number;
    isTruckSelectable?: boolean;
    stepMode?: number;
    currentDate: Date;
}

const TrucksPlannedCell: React.FC<TrucksPlannedCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    refetch = () => {},
    hiddenPlusIcon = false,
    group,
    isTruckSelectable = true,
    stepMode = stepModes.PLANNING,
    currentDate,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedId, setSelectedId] = useState('');
    const [selectedTransfer, setSelectedTransfer] = useState<DailyTransfer | null>(null);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedTruckPlanned, setSelectedTruckPlanned] = useState<DailyTruckPlanned | null>(null);
    const itemType = 'TRUCKSPLANNED';
    const itemType2 = 'TRANSFERSPLANNED';
    const { t } = useTranslation();
    const { muiTheme } = useTheme();

    const { mutate: updateTruckPlannedRankUpDown } = useUpdateDailyTruckPlannedRankUpDownMutation({ onSettled: () => refetch() });

    const { mutate: updateDailyTransferPlanned } = useUpdateDailyTransferMutation({
        onSettled: () => {
            refetch();
        },
    });

    const handleTrucksPlannedChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            await updateDailyTruckPlanned(fromItemId, { activityId: toLineId }, { autoTruck: true });
            refetch();
        } catch (error) {
            console.error('Error updating truck planned:', error);
        }
    };

    const handleTransferPlannedChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            updateDailyTransferPlanned(
                { DailyTransferId: fromItemId, data: { dailyTrucksPlannedId: toLineId } },
                {
                    onSuccess: async () => {
                        const tmpTruckPlanned = activities.flatMap((value) => value.dailyTrucksPlanned).find((value) => value._id === fromLineId);
                        if (tmpTruckPlanned && tmpTruckPlanned.mode === truckMode.TRANSFER && tmpTruckPlanned.transfers.length <= 1)
                            await deleteDailyTruckPlanned(fromLineId);
                    },
                }
            );
        } catch (error) {
            console.error('Error updating truck planned:', error);
        }
    };

    const handleDeleteTruckPlanned = async (id: string, index: number) => {
        try {
            await deleteDailyTruckPlanned(id);
            refetch();
        } catch (error) {
            console.error('Error deleting truck planned:', error);
        }
    };

    const handleCreateDailyTruckPlanned = async (dailyTruckPlannedId: string) => {
        const selectedTruckPlanned = activity.dailyTrucksPlanned.find((value) => value._id === dailyTruckPlannedId);
        if (selectedTruckPlanned) {
            const response = await createDailyTruckPlanned({
                activityId: activity._id,
                trucksAmount: selectedTruckPlanned.trucksAmount,
                departureLoadNatureId: selectedTruckPlanned.departureLoadNatureId?._id,
                arrivalLoadNatureId: selectedTruckPlanned.arrivalLoadNatureId?._id,
                departureDumpId: selectedTruckPlanned.departureDumpId?._id,
                arrivalDumpId: selectedTruckPlanned.arrivalDumpId?._id,
                departureQuarryId: selectedTruckPlanned.departureQuarryId?._id,
                arrivalQuarryId: selectedTruckPlanned.arrivalQuarryId?._id,
                departureSiteId: selectedTruckPlanned.departureSiteId?._id,
                arrivalSiteId: selectedTruckPlanned.arrivalSiteId?._id,
                mode: selectedTruckPlanned.mode,
                ridesAmount: selectedTruckPlanned.ridesAmount,
                truckTypeId: selectedTruckPlanned.truckTypeId._id,
                truckCompanyId: selectedTruckPlanned.truckCompanyId?._id,
                observations: selectedTruckPlanned.observations,
                truckId: selectedTruckPlanned.truckId?._id,
            });
            for (const transfer of selectedTruckPlanned.transfers) {
                await createDailyTransfer({
                    dailyTrucksPlannedId: response.data._id,
                    state: transfer.state,
                    machineId: transfer.machineId?._id,
                    quarryId: transfer.quarryId?._id,
                    rentalCompanyId: transfer.rentalCompanyId?._id,
                    rentalName: transfer.rentalName,
                    siteId: transfer.siteId?._id,
                    toolId: transfer.toolId?._id,
                });
            }
            refetch();
        }
    };

    return (
        <div className={`${'text-black'}`} style={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1 }}>
            <DroppableCell
                dropId={{ lineId: activity._id }}
                moveCell={(fromLineId, fromItemId, toLineId) => handleTrucksPlannedChange(fromLineId, fromItemId, toLineId)}
                itemType={itemType}
                disabled={disabled}
            >
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addRoad"
                                onClick={() => {
                                    setShowModal(true);
                                    setSelectedId('');
                                }}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addTrucksRides'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c fx-fg-1">
                        {activity.dailyTrucksPlanned
                            .filter((value) => group === undefined || value.truckTypeId.group === group)
                            .sort((a, b) => {
                                if ((a.rank || 1) < (b.rank || 1)) return -1;
                                if ((a.rank || 1) > (b.rank || 1)) return 1;
                                if ((a.mode || -1) < (b.mode || -1)) return -1;
                                if ((a.mode || -1) > (b.mode || -1)) return 1;
                                return 0;
                            })
                            .map((truckPlanned, index, array) => (
                                <TruckPlannedItem
                                    key={truckPlanned._id}
                                    truckPlanned={truckPlanned}
                                    index={index}
                                    activityId={activity._id}
                                    activityReportTruckGroup={activity.reportId.truckId?.truckTypeId.group}
                                    stepMode={stepMode}
                                    disabled={disabled}
                                    group={group}
                                    refetch={refetch}
                                    setShowModal={setShowModal}
                                    setSelectedId={setSelectedId}
                                    setShowAssignModal={setShowAssignModal}
                                    setSelectedTruckPlanned={setSelectedTruckPlanned}
                                    setShowTransferModal={setShowTransferModal}
                                    setSelectedTransfer={setSelectedTransfer}
                                    updateTruckPlannedRankUpDown={updateTruckPlannedRankUpDown}
                                    handleCreateDailyTruckPlanned={handleCreateDailyTruckPlanned}
                                    activities={activities}
                                    isSpecial={activity.reportId.userId.isSpecial}
                                />
                            ))}
                    </div>
                </div>
            </DroppableCell>
            {showModal && (
                <ManageDailyTruckPlannedModal
                    open={showModal}
                    activity={activity}
                    handleClose={() => setShowModal(false)}
                    refetch={refetch}
                    truckPlannedId={selectedId}
                    truckType={group}
                    isTruckSelectable={isTruckSelectable}
                    stepMode={stepMode}
                />
            )}
            {showAssignModal && selectedTruckPlanned && (
                <AssignTruckPlannedModal
                    dailyTruckPlanned={selectedTruckPlanned}
                    open={showAssignModal}
                    handleClose={() => setShowAssignModal(false)}
                    refetch={refetch}
                    activities={activities}
                    currentDate={currentDate}
                />
            )}
            {showTransferModal && (
                <ManageDailyTransferModal
                    handleClose={() => setShowTransferModal(false)}
                    open={showTransferModal}
                    refetch={refetch}
                    truckPlannedId={selectedId}
                    transfer={selectedTransfer}
                    siteName={activity.siteId.name}
                />
            )}
        </div>
    );
};
export default TrucksPlannedCell;
